// src/services/supportService.js
// Everything the /support page needs that isn't UI: the problem picker, the
// context passed to the ElevenLabs agent, and the two backend calls.
//
// The agent never sees the ElevenLabs key or agent ID — the backend hands out
// a short-lived signed URL. Contacting support now requires sign-in (tickets
// are tracked per student); the only unauthenticated call is cant-signin,
// the small email form on the login page. All calls go through the single
// router `/api/support?action=<name>`.
import { auth } from '../config/firebase';
import { isPausedError } from './supportFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

// One switch for the AI chat. While true the "Chat with our AI agent" and
// voice-test buttons are greyed out (ElevenLabs quota used up). The server
// can also say so itself with a 503 {paused:true}, which is honoured too.
export const AI_CHAT_PAUSED = true;

// Two-step problem picker. Topic ids must match TOPIC_CATEGORY in the
// backend's api/support-contact.js. `team: true` topics skip the AI agent and
// go straight to a person (privacy requests, safety reports).
export const TOPICS = [
  {
    id: 'signing_in', label: 'Signing in',
    subtopics: [
      { id: 'forgot_password', label: 'Forgot password' },
      { id: 'google_fails', label: 'Google sign-in fails' },
      { id: 'phone_fails', label: 'Phone sign-in fails' },
      { id: 'no_account', label: 'Wrong method / "no account found"' },
      { id: 'lost_access', label: "Can't access my email or phone" },
      { id: 'someone_else', label: 'Someone else is using my account' },
    ],
  },
  {
    id: 'verification_code', label: 'Verification code',
    subtopics: [
      { id: 'code_missing', label: "Code didn't arrive" },
      { id: 'code_expired', label: 'Code says expired or wrong' },
      { id: 'code_new_device', label: 'New-device code' },
    ],
  },
  {
    id: 'page_problem', label: 'Page or app problem',
    subtopics: [
      { id: 'blank', label: 'Blank or stuck loading' },
      { id: 'phone_layout', label: 'Looks broken on my phone' },
      { id: 'slow', label: 'Very slow' },
    ],
  },
  {
    id: 'ai_features', label: 'AI features',
    subtopics: [
      { id: 'ai_match', label: 'AI Match not responding' },
      { id: 'ai_chat', label: 'AI chat error' },
      { id: 'ai_off', label: 'AI is turned off (trial ended)' },
    ],
  },
  {
    id: 'opportunities', label: 'Opportunities',
    subtopics: [
      { id: 'wrong_info', label: 'Wrong information' },
      { id: 'broken_link', label: 'Broken link' },
      { id: 'deadline_passed', label: 'Deadline already passed' },
    ],
  },
  {
    id: 'my_progress', label: 'My progress',
    subtopics: [
      { id: 'status_not_saving', label: 'Application status not saving' },
      { id: 'streak', label: 'Streak not updating' },
    ],
  },
  {
    id: 'privacy', label: 'Privacy or my data', team: true,
    subtopics: [
      { id: 'delete_data', label: 'Delete my account or data' },
      { id: 'guardian_request', label: 'Parent or guardian request' },
    ],
  },
  {
    id: 'report', label: 'Report a problem', team: true,
    subtopics: [
      { id: 'abuse', label: 'Abuse or bullying' },
      { id: 'unsafe', label: 'Something else unsafe' },
    ],
  },
  { id: 'other', label: 'Something else', subtopics: [] },
];

export function findTopic(id) {
  return TOPICS.find((t) => t.id === id) || null;
}
export function findSubtopic(topic, id) {
  return topic?.subtopics.find((s) => s.id === id) || null;
}

// "iPhone Safari", "Windows Chrome" — coarse on purpose. Enough for the agent
// to pick the right fix; not a fingerprint.
export function deviceLabel() {
  const ua = navigator.userAgent || '';
  let os = 'Unknown device';
  if (/iPhone|iPad|iPod/.test(ua)) os = 'iPhone/iPad';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'Mac';
  else if (/Linux/.test(ua)) os = 'Linux';
  let browser = 'browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/Firefox\/|FxiOS/.test(ua)) browser = 'Firefox';
  else if (/Chrome\/|CriOS/.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  return `${os} ${browser}`;
}

function accountAge(user) {
  const created = user?.metadata?.creationTime ? new Date(user.metadata.creationTime).getTime() : NaN;
  if (Number.isNaN(created)) return 'unknown';
  const hours = Math.floor((Date.now() - created) / 3600000);
  if (hours < 1) return 'under an hour';
  if (hours < 48) return `${hours} hours`;
  return `${Math.floor(hours / 24)} days`;
}

const PROVIDER_NAMES = { 'google.com': 'google', password: 'email', phone: 'phone' };
function signInMethod(user) {
  const names = (user?.providerData || []).map((p) => PROVIDER_NAMES[p.providerId]).filter(Boolean);
  return names.length ? [...new Set(names)].join('+') : 'unknown';
}

// Names here must match the dynamic variables defined on the ElevenLabs
// agent, character for character (they're case-sensitive). All values are
// strings: a missing one falls back to the default set on the agent, so
// every field is always sent.
export function buildDynamicVariables({ firstName, email, topic, subtopic, details, user, profile, tier }) {
  const signedIn = !!user;
  return {
    user_name: firstName || 'there',
    user_email: email || 'not provided',
    signed_in: signedIn ? 'yes' : 'no',
    topic: topic?.label || 'general',
    subtopic: subtopic?.label || (topic?.id === 'other' ? 'something else' : 'general'),
    plan: signedIn ? (tier === 'pro' ? 'pro' : 'free') : 'unknown',
    // The 7-day AI trial isn't built yet, so there's nothing real to report.
    trial_status: 'unknown',
    sign_in_method: signedIn ? signInMethod(user) : 'unknown',
    email_verified: signedIn ? (profile?.signupVerified ? 'yes' : 'no') : 'unknown',
    account_age: signedIn ? accountAge(user) : 'unknown',
    device: deviceLabel(),
    current_page: 'support',
    issue_details: (details || '').trim().slice(0, 1500) || 'none',
    // Only used by the create_support_ticket tool. Not verified server-side.
    user_uid: user?.uid || '',
  };
}

async function optionalAuthHeader() {
  try {
    const token = await auth.currentUser?.getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

async function post(path, body) {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await optionalAuthHeader()) },
    body: JSON.stringify(body || {}),
  });
  let data = {};
  try { data = await res.json(); } catch { /* non-JSON error body */ }
  if (!res.ok) {
    const err = new Error(data.message || data.error || 'Something went wrong');
    err.status = res.status;
    err.paused = isPausedError(res.status, data);
    throw err;
  }
  return data;
}

// mode 'voice' is gated server-side (owner only for now); anything else is text.
export async function getSupportSession(mode = 'text') {
  const { signedUrl } = await post('/api/support?action=session', { mode });
  return signedUrl;
}

// Returns { ticketId, openTicketId }. openTicketId is set when the student
// already had an open ticket; the new one is still created.
export async function submitContact(payload) {
  const { ticketId, openTicketId } = await post('/api/support?action=contact', payload);
  return { ticketId, openTicketId: openTicketId || '' };
}

// Permanent customer number, e.g. { customerId: '482913', customerNumber: 'EF-482913', plan }.
export function ensureCustomer() {
  return post('/api/support?action=ensure-customer', {});
}

export async function fetchMyTickets() {
  const { tickets } = await post('/api/support?action=my-tickets', {});
  return Array.isArray(tickets) ? tickets : [];
}

// Only allowed server-side when the ticket is resolved or closed.
export async function relaunchTicket(ticketId) {
  const data = await post('/api/support?action=relaunch', { ticketId });
  // The backend answers { ticketId, linkedTicketId }; the page reads .id.
  return { id: data.ticketId || data.ticket?.id, ...(data.ticket || {}) };
}

// "Can't sign in?" form on the login page. No account needed, so no token.
export async function sendCantSignIn({ name, email, message, hp }) {
  const res = await fetch(`${BACKEND_URL}/api/support?action=cant-signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, message, hp: hp || '' }),
  });
  let data = {};
  try { data = await res.json(); } catch { /* non-JSON error body */ }
  if (!res.ok) throw new Error(data.error || 'Could not send right now. Please try again in a moment.');
  return true;
}

// The form, chat and ticket screens are separate pages, so what the visitor
// typed has to survive the hop between them (and a refresh). sessionStorage:
// per tab, gone when the tab closes, never sent anywhere.
const CTX_KEY = 'elpis_support_ctx';
const TICKET_KEY = 'elpis_support_ticket';

function store(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* storage blocked: the page still works, just not across a refresh */ }
}
function load(key) {
  try { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
}

// { firstName, lastName, email, topicId, subtopicId, details, mode: 'text' | 'voice' }
export const saveSupportContext = (ctx) => store(CTX_KEY, ctx);
export const loadSupportContext = () => load(CTX_KEY);
// { id, name, email, topicLabel, subtopicLabel, details, sentAt, openTicketId?, customerNumber? }
export const saveSupportTicket = (ticket) => store(TICKET_KEY, ticket);
export const loadSupportTicket = () => load(TICKET_KEY);
export function clearSupportSession() {
  try { sessionStorage.removeItem(CTX_KEY); sessionStorage.removeItem(TICKET_KEY); } catch { /* nothing to clear */ }
}

// Files the "Contact the team" ticket from saved form context and records the
// result for the ticket page. Shared by the form page and the chat page.
export async function sendTicketFromContext(ctx, { extraDetails = '', customerNumber = '' } = {}) {
  const topic = findTopic(ctx.topicId);
  const subtopic = findSubtopic(topic, ctx.subtopicId);
  const details = [ctx.details, extraDetails].filter(Boolean).join('\n\n').trim();
  const { ticketId: id, openTicketId } = await submitContact({
    firstName: ctx.firstName, lastName: ctx.lastName, email: ctx.email,
    topic: topic?.id || '', topicLabel: topic?.label || '',
    subtopic: subtopic?.id || '', subtopicLabel: subtopic?.label || '',
    details, device: deviceLabel(), website: '',
  });
  const ticket = {
    id,
    name: [ctx.firstName, ctx.lastName].filter(Boolean).join(' '),
    email: ctx.email,
    topicLabel: topic?.label || '',
    subtopicLabel: subtopic?.label || '',
    details: ctx.details || '',
    sentAt: new Date().toISOString(),
    openTicketId,
    customerNumber,
  };
  saveSupportTicket(ticket);
  return ticket;
}
